/**
 * UCX-09: runtime 設定注入。
 *
 * Vite の import.meta.env は build 時埋め込みのため、1社1デプロイで image を
 * 顧客非依存に保つには実行時注入が必要。コンテナ entrypoint
 * (deploy/web-ui-entrypoint.sh) が env から dist/runtime-config.js を生成し、
 * index.html が読み込んだ window.__CH_RUNTIME_CONFIG__ を build 時値より優先する。
 */
export interface ChRuntimeConfig {
  VITE_CLERK_PUBLISHABLE_KEY?: string
  VITE_MCP_SERVER_URL?: string
  /** WU-04: ui.enabledScreens。カンマ区切り or JSON 配列の screen id。未設定 = 全画面有効 */
  CH_UI_ENABLED_SCREENS?: string
  /** WU-08b: ui.defaultScreen。ルート "/" 到達時の初期表示 screen id。未設定 = 現行挙動 */
  CH_UI_DEFAULT_SCREEN?: string
  /**
   * WU-08c: ui.glossary。`{"用語":"説明"}` の JSON object 文字列。
   * 顧客固有の業務用語の説明文はコード・バンドルに焼き込まない（黄金律）ため、
   * deploy env からのみ注入する。未設定 = 現行挙動（tooltip 非表示）維持
   */
  CH_UI_GLOSSARY?: string
}

/**
 * WU-04: entrypoint (deploy/web-ui-entrypoint.sh) が注入してよいキーの正本。
 * 契約テスト (runtime-config-allowlist.test.ts) が entrypoint と同期を検証する。
 * auth 系キー（VITE_ENABLE_PREVIEW_AUTH 等）は追加禁止。
 */
export const RUNTIME_CONFIG_ALLOWLIST = [
  'VITE_CLERK_PUBLISHABLE_KEY',
  'VITE_MCP_SERVER_URL',
  'CH_UI_ENABLED_SCREENS',
  'CH_UI_DEFAULT_SCREEN',
  'CH_UI_GLOSSARY',
] as const satisfies readonly (keyof ChRuntimeConfig)[]

declare global {
  interface Window {
    __CH_RUNTIME_CONFIG__?: ChRuntimeConfig
  }
}

export function runtimeConfigValue(
  key: keyof ChRuntimeConfig,
  buildTimeValue: string | undefined,
): string | undefined {
  const runtime =
    typeof window !== 'undefined' ? window.__CH_RUNTIME_CONFIG__ : undefined
  const injected = runtime?.[key]
  if (typeof injected === 'string' && injected.trim() !== '') {
    return injected
  }
  if (typeof buildTimeValue === 'string' && buildTimeValue.trim() !== '') {
    return buildTimeValue
  }
  return undefined
}

/**
 * WU-04: ui.enabledScreens の解釈。
 * - 未設定 / 空 / 空要素のみ / 壊れた JSON → null（全画面有効 = 既存挙動不変。
 *   誤設定での全ロックアウトを防ぐ。権限境界はサーバー 403 側）
 * - カンマ区切り（`dashboard,kpi`）or JSON 配列（`["dashboard","kpi"]`）→ セット
 */
export function parseEnabledScreens(
  raw: string | undefined,
): ReadonlySet<string> | null {
  if (typeof raw !== 'string') {
    return null
  }
  const trimmed = raw.trim()
  if (trimmed === '') {
    return null
  }

  let ids: string[]
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed)
      if (!Array.isArray(parsed)) {
        return null
      }
      ids = parsed.filter((v): v is string => typeof v === 'string')
    } catch {
      return null
    }
  } else {
    ids = trimmed.split(',')
  }

  const screens = new Set(ids.map((id) => id.trim()).filter((id) => id !== ''))
  return screens.size > 0 ? screens : null
}

/** runtime 注入された ui.enabledScreens を読む。未注入 = null = 全画面有効 */
export function enabledScreens(): ReadonlySet<string> | null {
  const runtime =
    typeof window !== 'undefined' ? window.__CH_RUNTIME_CONFIG__ : undefined
  return parseEnabledScreens(runtime?.CH_UI_ENABLED_SCREENS)
}

/**
 * WU-08b: runtime 注入された ui.defaultScreen（CH_UI_DEFAULT_SCREEN）を読む。
 * 未注入 / 空白のみ = undefined = 現行挙動維持。値の妥当性検証（screen id か、
 * 有効化済みか）は呼び出し側（nav-config.ts の resolveDefaultLandingHref）が行う。
 */
export function defaultScreen(): string | undefined {
  const runtime =
    typeof window !== 'undefined' ? window.__CH_RUNTIME_CONFIG__ : undefined
  const raw = runtime?.CH_UI_DEFAULT_SCREEN
  if (typeof raw !== 'string') {
    return undefined
  }
  const trimmed = raw.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * WU-08c: ui.glossary（CH_UI_GLOSSARY）の解釈。
 * - 未設定 / 空白のみ → null（tooltip 非表示 = 現行挙動維持）
 * - 壊れた JSON / object 以外（配列・null・プリミティブ）→ console.warn して null
 *   （fail-safe。誤設定で画面を壊さない）
 * - 値が string でないエントリは除去し、string のエントリだけを残す
 */
export function parseGlossary(raw: string | undefined): Record<string, string> | null {
  if (typeof raw !== 'string') {
    return null
  }
  const trimmed = raw.trim()
  if (trimmed === '') {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    console.warn('[runtime-config] CH_UI_GLOSSARY: invalid JSON, ignoring')
    return null
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    console.warn('[runtime-config] CH_UI_GLOSSARY: expected a JSON object, ignoring')
    return null
  }

  const entries = Object.entries(parsed as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  )
  return Object.fromEntries(entries)
}

/** runtime 注入された ui.glossary を読む。未注入 / 不正 = null */
export function glossary(): Record<string, string> | null {
  const runtime =
    typeof window !== 'undefined' ? window.__CH_RUNTIME_CONFIG__ : undefined
  return parseGlossary(runtime?.CH_UI_GLOSSARY)
}
